import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Loader2, Trophy } from "lucide-react";

interface LeaderboardRow {
  rank: number;
  creator_name: string | null;
  gmv: number;
  video_count: number;
  computed_at: string | null;
  [key: string]: unknown;
}

interface LeaderboardData {
  campaign: {
    brand_name: string;
    product_name: string | null;
    campaign_start_date: string | null;
    campaign_end_date: string | null;
    prize_pool: { place: number; amount: number }[] | null;
  };
  leaderboard: LeaderboardRow[];
  my_rank: LeaderboardRow | null;
}

const MEDAL: Record<number, string> = { 1: "🥇", 2: "🥈", 3: "🥉" };

function fmt(n: number) {
  return "$" + Math.round(n).toLocaleString();
}

function fmtDate(s: string | null | undefined) {
  if (!s) return null;
  return new Date(s).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

export default function PublicLeaderboard() {
  const params = useParams();
  const token = params.token;

  const { data, isLoading, error } = useQuery<LeaderboardData>({
    queryKey: ["public-leaderboard", token],
    queryFn: async () => {
      const res = await fetch(`/api/v1/hiring/leaderboard/${token}`);
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || "Failed to load leaderboard");
      }
      return res.json();
    },
    enabled: !!token,
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-sm max-w-md w-full p-8 text-center">
          <div className="text-4xl mb-4">🔗</div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Leaderboard Unavailable</h2>
          <p className="text-gray-500 text-sm">
            {(error as Error)?.message || "This leaderboard link is invalid or the campaign doesn't have rankings yet."}
          </p>
        </div>
      </div>
    );
  }

  const { campaign, leaderboard, my_rank } = data;
  const startDate = fmtDate(campaign.campaign_start_date);
  const endDate = fmtDate(campaign.campaign_end_date);
  const firstPlacePrize = campaign.prize_pool?.find((p) => p.place === 1)?.amount ?? null;

  return (
    <div className="min-h-screen bg-slate-100 py-8 px-4">
      <div className="max-w-lg mx-auto space-y-4">

        {/* Header */}
        <div className="text-center">
          <img src="/bmc-logo.png" alt="BMC" className="h-8 mx-auto mb-4" />
        </div>

        {/* Campaign banner */}
        <div className="bg-white rounded-xl shadow-sm p-5">
          <div className="flex items-center gap-3 mb-2">
            <div className="rounded-full bg-amber-100 p-2">
              <Trophy className="h-5 w-5 text-amber-600" />
            </div>
            <div>
              <h1 className="text-base font-bold text-gray-900">{campaign.brand_name} Rewards Leaderboard</h1>
              {campaign.product_name && (
                <p className="text-xs text-gray-500">{campaign.product_name}</p>
              )}
            </div>
          </div>
          {(startDate || endDate) && (
            <p className="text-xs text-gray-400 mt-1 ml-11">
              {startDate && endDate ? `${startDate} – ${endDate}` : startDate || endDate}
            </p>
          )}
          {firstPlacePrize && (
            <p className="text-xs text-[#FB3AAE] font-semibold mt-1 ml-11">
              🏆 First place wins up to ${firstPlacePrize.toLocaleString()}
            </p>
          )}
        </div>

        {/* My rank highlight */}
        {my_rank ? (
          <div className="bg-[#FB3AAE]/10 border border-[#FB3AAE]/25 rounded-xl p-4">
            <p className="text-[10px] font-bold text-[#FB3AAE] uppercase tracking-wider mb-2">Your Current Rank</p>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="text-3xl font-black text-[#FB3AAE]">
                  {MEDAL[my_rank.rank] || `#${my_rank.rank}`}
                </span>
                <div>
                  <p className="text-sm font-bold text-gray-900">{my_rank.creator_name || "You"}</p>
                  <p className="text-xs text-gray-500">{my_rank.video_count} video{my_rank.video_count !== 1 ? "s" : ""}</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-lg font-black text-gray-900">{fmt(my_rank.gmv)}</p>
                <p className="text-[10px] text-gray-400">GMV generated</p>
              </div>
            </div>
            {campaign.prize_pool && (() => {
              const prize = campaign.prize_pool!.find((p) => p.place === my_rank.rank);
              return prize ? (
                <div className="mt-2 pt-2 border-t border-[#FB3AAE]/20 text-xs text-[#FB3AAE] font-semibold">
                  🎉 You're on track to win ${prize.amount.toLocaleString()} in prize money!
                </div>
              ) : null;
            })()}
          </div>
        ) : (
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-center">
            <p className="text-sm font-medium text-gray-700 mb-1">You're not ranked yet</p>
            <p className="text-xs text-gray-400">Keep posting videos to appear on the leaderboard. Rankings update periodically.</p>
          </div>
        )}

        {/* Full leaderboard */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-100">
            <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">Full Rankings</p>
          </div>
          {leaderboard.length === 0 ? (
            <div className="p-8 text-center">
              <p className="text-sm text-gray-500">No rankings yet. Check back after the campaign starts.</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {leaderboard.map((row) => {
                const isMe = my_rank != null && row.creator_name != null && row.creator_name === my_rank.creator_name;
                return (
                  <div
                    key={row.rank}
                    className={`flex items-center px-4 py-3 gap-3 ${isMe ? "bg-[#FB3AAE]/5" : ""}`}
                  >
                    <span className="w-8 text-center text-base shrink-0">
                      {MEDAL[row.rank] || <span className="text-sm font-bold text-gray-400">#{row.rank}</span>}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-medium truncate ${isMe ? "text-[#FB3AAE]" : "text-gray-900"}`}>
                        {row.creator_name || "Creator"}
                        {isMe && <span className="ml-1 text-[10px] font-bold uppercase tracking-wide">(You)</span>}
                      </p>
                      <p className="text-xs text-gray-400">{row.video_count} video{row.video_count !== 1 ? "s" : ""}</p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-sm font-bold text-gray-900">{fmt(row.gmv)}</p>
                      {campaign.prize_pool?.find((p) => p.place === row.rank) && (
                        <p className="text-[10px] text-amber-600 font-semibold">
                          ${campaign.prize_pool!.find((p) => p.place === row.rank)!.amount.toLocaleString()} prize
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          {leaderboard.length > 0 && leaderboard[0].computed_at && (
            <div className="px-4 py-2 border-t border-slate-100 text-center">
              <p className="text-[10px] text-gray-400">
                Last updated {new Date(leaderboard[0].computed_at).toLocaleString()}
              </p>
            </div>
          )}
        </div>

        <p className="text-center text-xs text-gray-400 pb-4">
          Questions? Reply to the offer email and our team will help.
        </p>
      </div>
    </div>
  );
}
